import {
    deploy_in_progress,
    deployed,
    distillation_in_progress,
    evaluation_in_progress,
    ready_to_deploy,
    undeploy_in_progress,
} from '@/shared/constants/taskStatuses';
import { hasAnyTaskConfig } from './promptPropagation';

/** Task page tab `action` query values (match TASK_PAGE_TABS keys). */
export const TASK_DRAWER_ACTION = {
    MODEL_SETUP: 'model-setup',
    DATA_SELECTION: 'data-selection',
    EVALUATION: 'evaluation-from-session',
};

const EVALUATION_INFO_STATUSES = new Set([
    evaluation_in_progress,
    ready_to_deploy,
    deploy_in_progress,
    deployed,
    undeploy_in_progress,
]);

/** Statuses during which the task's evaluation results are being (re)built. */
const EVALUATION_PENDING_STATUSES = new Set([
    distillation_in_progress,
    evaluation_in_progress,
]);

const hasConfiguredValue = (value) => {
    if (typeof value === 'string') return value.trim().length > 0;
    return value != null && value !== false;
};

const hasDataset = (usecase) =>
    Array.isArray(usecase?.datasets) && usecase.datasets.length > 0;

const isModelConfigurationCompleted = (usecase) => {
    const comparisonOn = Boolean(usecase?.comparison_mode);
    const hasPrompt = hasConfiguredValue(usecase?.prompt);
    const hasTeacherModel = hasConfiguredValue(usecase?.teacher_model);
    const hasParserFunction = hasConfiguredValue(
        usecase?.parser_function || usecase?.parserFunction
    );
    return hasPrompt && (!comparisonOn || (hasTeacherModel && hasParserFunction));
};

const isEmptyTask = (usecase) => {
    const hasPrompt = hasConfiguredValue(usecase?.prompt);
    const hasTeacher = hasConfiguredValue(usecase?.teacher_model);
    return !hasDataset(usecase) && !hasPrompt && !hasTeacher;
};

export const hasEvaluationInfo = (usecase, evaluationResultsReady) => {
    if (usecase?.status && EVALUATION_INFO_STATUSES.has(usecase.status)) {
        return true;
    }
    if (!usecase?._id) return false;
    return Object.keys(evaluationResultsReady || {}).some((key) =>
        key.startsWith(`${usecase._id}_`)
    );
};

/**
 * True only once the task has evaluation *results* to show: the task is in a
 * post-training status, the `evaluation` presence probe
 * (`useEvaluationPresence`) confirmed results, and no training or evaluation
 * is running. Unlike `hasEvaluationInfo`, a status such as "Ready to Deploy"
 * or a ready-signal key (which is also set for failed runs) does not count on
 * its own, so this starts false and only becomes true after confirmation.
 */
export const hasEvaluationResults = (usecase, evalPresenceByUsecaseId) => {
    if (!usecase?._id) return false;
    if (!usecase.status || !EVALUATION_INFO_STATUSES.has(usecase.status)) {
        return false;
    }
    if (EVALUATION_PENDING_STATUSES.has(usecase.status)) return false;
    return evalPresenceByUsecaseId?.[String(usecase._id)] === true;
};

/**
 * Picks the initial task page tab. When the task has no dataset, land on the
 * default Data Overview tab so the user adds data first. When model config is
 * complete, also stay on Data Overview (training lives on the creation
 * wizard and Retrain). When model config is incomplete and the task has no
 * prompt or teacher yet, stay on Data Overview so the empty-state upload
 * flow is not competed with by Model Configuration.
 *
 * @param {Object|null|undefined} usecase
 * @param {{ isMultiObjective?: boolean, evaluationResultsReady?: Object }} [options]
 * @returns {string|null} Tab key for `?action=` or null for the default tab.
 */
export function deriveInitialTaskAction(
    usecase,
    { isMultiObjective = false, evaluationResultsReady = {} } = {}
) {
    if (isMultiObjective || !usecase) return null;
    if (isEmptyTask(usecase)) return null;
    if (hasEvaluationInfo(usecase, evaluationResultsReady)) {
        return TASK_DRAWER_ACTION.EVALUATION;
    }
    if (!hasDataset(usecase)) {
        return null;
    }
    if (isModelConfigurationCompleted(usecase) || !hasAnyTaskConfig(usecase)) {
        return null;
    }
    return TASK_DRAWER_ACTION.MODEL_SETUP;
}
